import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  TrendingUp,
  Activity,
  MapPin,
  AlertCircle,
  CheckCircle,
  ArrowRight,
  BarChart3,
  Globe,
  Target,
  Search
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { getDateRange } from '@/lib/google-ads-client';

interface WebVitalsStatus {
  site: string;
  url: string;
  latestScore: number | null;
  lastChecked: string | null;
  status: 'good' | 'needs-improvement' | 'poor' | 'unchecked';
}

interface CompetitorStats {
  totalCompetitors: number;
  byCounty: { county: string; count: number }[];
  lastScan: string | null;
}

export default function MarketIntelligencePage() {
  const [webVitalsStatus, setWebVitalsStatus] = useState<WebVitalsStatus[]>([
    { site: 'Hales AC', url: 'https://halesac.com', latestScore: null, lastChecked: null, status: 'unchecked' },
    { site: 'My CCS', url: 'https://my-ccs.com', latestScore: null, lastChecked: null, status: 'unchecked' },
    { site: 'Abraham AC', url: 'https://abrahamac.com', latestScore: null, lastChecked: null, status: 'unchecked' },
  ]);
  const [competitorStats, setCompetitorStats] = useState<CompetitorStats>({
    totalCompetitors: 0,
    byCounty: [],
    lastScan: null,
  });
  const [keywordCount, setKeywordCount] = useState<number>(0);
  const [gbpLocationCount, setGbpLocationCount] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [paidSearchIntel, setPaidSearchIntel] = useState<any>(null);

  useEffect(() => {
    loadDashboardData();
    loadPaidSearchIntelligence();
  }, []);

  async function loadDashboardData() {
    try {
      // Load Web Vitals status - get ONLY the most recent record for each URL
      const siteUrls = ['https://halesac.com', 'https://my-ccs.com', 'https://abrahamac.com'];
      const vitalsPromises = siteUrls.map(url =>
        supabase
          .from('core_web_vitals')
          .select('page_url, performance_score, date')
          .eq('page_url', url)
          .order('date', { ascending: false })
          .limit(1)
          .single()
      );

      const vitalsResults = await Promise.all(vitalsPromises);
      
      const updatedStatus = webVitalsStatus.map((site, index) => {
        const result = vitalsResults[index];
        if (result.data) {
          let status: 'good' | 'needs-improvement' | 'poor' = 'poor';
          if (result.data.performance_score >= 90) status = 'good';
          else if (result.data.performance_score >= 50) status = 'needs-improvement';

          return {
            ...site,
            latestScore: result.data.performance_score,
            lastChecked: result.data.date,
            status,
          };
        }
        return site;
      });

      setWebVitalsStatus(updatedStatus);

      // Load Competitor stats
      const { data: competitorData } = await supabase
        .from('competitor_locations')
        .select('county, last_updated');

      if (competitorData) {
        const countyMap = new Map<string, number>();
        let lastScan: string | null = null;

        competitorData.forEach((record) => {
          countyMap.set(record.county, (countyMap.get(record.county) || 0) + 1);
          if (!lastScan || record.last_updated > lastScan) {
            lastScan = record.last_updated;
          }
        });

        const byCounty = Array.from(countyMap.entries())
          .map(([county, count]) => ({ county, count }))
          .sort((a, b) => b.count - a.count);

        setCompetitorStats({
          totalCompetitors: competitorData.length,
          byCounty,
          lastScan,
        });
      }

      // Load Keyword count from keyword_demand table
      const { count: keywordCountResult } = await supabase
        .from('keyword_demand')
        .select('*', { count: 'exact', head: true });
      
      setKeywordCount(keywordCountResult || 0);

      // Load GBP location count from google_business_profiles table
      const { count: gbpCountResult } = await supabase
        .from('google_business_profiles')
        .select('*', { count: 'exact', head: true });
      
      setGbpLocationCount(gbpCountResult || 0);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }

  async function loadPaidSearchIntelligence() {
    try {
      const range = getDateRange(30);
      
      // Load search query data
      const { data: searchQueries } = await supabase
        .from('google_ads_search_queries')
        .select('*')
        .gte('date', range.startDate)
        .lte('date', range.endDate)
        .order('impressions', { ascending: false })
        .limit(10);

      // Load top performing keywords
      const { data: topKeywords } = await supabase
        .from('google_ads_keyword_performance')
        .select('*')
        .gte('date', range.startDate)
        .lte('date', range.endDate)
        .order('conversions', { ascending: false })
        .limit(5);

      if (searchQueries || topKeywords) {
        setPaidSearchIntel({
          topSearchTerms: searchQueries || [],
          topKeywords: topKeywords || [],
          totalSearchTerms: searchQueries?.length || 0,
          totalKeywords: topKeywords?.length || 0
        });
      }
    } catch (error) {
      console.error('Error loading paid search intelligence:', error);
    }
  }

  const overallWebVitalsHealth = () => {
    const checked = webVitalsStatus.filter(s => s.status !== 'unchecked');
    if (checked.length === 0) return 'No data';
    
    const good = checked.filter(s => s.status === 'good').length;
    const needsImprovement = checked.filter(s => s.status === 'needs-improvement').length;
    
    if (good === checked.length) return 'Excellent';
    if (good > 0 && needsImprovement === 0) return 'Good';
    if (needsImprovement > 0) return 'Needs Attention';
    return 'Critical';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Market Intelligence</h1>
        <p className="text-gray-600">
          Google API-powered insights for Florida HVAC market dominance
        </p>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Activity className="w-6 h-6 text-blue-600" />
            </div>
            <span className={`text-sm font-medium px-3 py-1 rounded-full ${
              overallWebVitalsHealth() === 'Excellent' ? 'bg-green-100 text-green-800' :
              overallWebVitalsHealth() === 'Good' ? 'bg-blue-100 text-blue-800' :
              overallWebVitalsHealth() === 'Needs Attention' ? 'bg-yellow-100 text-yellow-800' :
              overallWebVitalsHealth() === 'Critical' ? 'bg-red-100 text-red-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {overallWebVitalsHealth()}
            </span>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-1">
            {webVitalsStatus.filter(s => s.status !== 'unchecked').length}/3
          </h3>
          <p className="text-sm text-gray-600 mb-4">Sites Monitored</p>
          <Link
            to="/web-vitals"
            className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
          >
            View Core Web Vitals
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <MapPin className="w-6 h-6 text-purple-600" />
            </div>
            <span className="text-sm font-medium px-3 py-1 rounded-full bg-purple-100 text-purple-800">
              {competitorStats.byCounty.length} Counties
            </span>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-1">
            {competitorStats.totalCompetitors}
          </h3>
          <p className="text-sm text-gray-600 mb-4">Competitors Tracked</p>
          <Link
            to="/competitor-heatmap"
            className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
          >
            View Heatmap
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-green-600" />
            </div>
            <span className="text-sm font-medium px-3 py-1 rounded-full bg-green-100 text-green-800">
              {keywordCount} Keywords
            </span>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-1">
            Market Demand
          </h3>
          <p className="text-sm text-gray-600 mb-4">Keyword & Trends</p>
          <Link
            to="/keyword-demand"
            className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
          >
            View Keywords
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-orange-600" />
            </div>
            <span className="text-sm font-medium px-3 py-1 rounded-full bg-orange-100 text-orange-800">
              {gbpLocationCount} Locations
            </span>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-1">
            GBP Performance
          </h3>
          <p className="text-sm text-gray-600 mb-4">Business Profiles</p>
          <Link
            to="/gbp-dashboard"
            className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
          >
            View Dashboard
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* Web Vitals Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5" />
            Core Web Vitals Status
          </h2>
          <div className="space-y-4">
            {webVitalsStatus.map((site) => (
              <div key={site.site} className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {site.status === 'good' && <CheckCircle className="w-5 h-5 text-green-600" />}
                  {site.status === 'needs-improvement' && <AlertCircle className="w-5 h-5 text-yellow-600" />}
                  {site.status === 'poor' && <AlertCircle className="w-5 h-5 text-red-600" />}
                  {site.status === 'unchecked' && <Globe className="w-5 h-5 text-gray-400" />}
                  <div>
                    <p className="font-medium text-gray-900">{site.site}</p>
                    <p className="text-sm text-gray-500">{site.url}</p>
                  </div>
                </div>
                <div className="text-right">
                  {site.latestScore !== null ? (
                    <>
                      <p className="font-bold text-lg text-gray-900">{site.latestScore}</p>
                      <p className="text-xs text-gray-500">
                        {site.lastChecked ? new Date(site.lastChecked).toLocaleDateString() : '-'}
                      </p>
                    </>
                  ) : (
                    <p className="text-sm text-gray-400">Not checked</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            Competitor Distribution
          </h2>
          {competitorStats.totalCompetitors > 0 ? (
            <>
              <div className="space-y-4 mb-4">
                {competitorStats.byCounty.map((county) => (
                  <div key={county.county}>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-700">{county.county}</span>
                      <span className="text-sm text-gray-900 font-bold">{county.count}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-purple-600 h-2 rounded-full"
                        style={{
                          width: `${(county.count / competitorStats.totalCompetitors) * 100}%`,
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
              {competitorStats.lastScan && (
                <p className="text-sm text-gray-500">
                  Last scan: {new Date(competitorStats.lastScan).toLocaleDateString()}
                </p>
              )}
            </>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <MapPin className="w-12 h-12 mx-auto mb-3 text-gray-400" />
              <p className="text-sm">No competitors tracked yet</p>
              <Link
                to="/competitor-heatmap"
                className="text-sm text-primary-600 hover:text-primary-700 font-medium mt-2 inline-block"
              >
                Start scanning counties
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Paid Search Intelligence */}
      {paidSearchIntel && paidSearchIntel.topSearchTerms.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2">
              <Target className="w-5 h-5 text-blue-600" />
              <h2 className="text-lg font-semibold text-gray-900">Paid Search Intelligence (Google Ads)</h2>
            </div>
            <Link
              to="/google-ads/search-queries"
              className="text-sm text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1"
            >
              View All
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <Search className="w-4 h-4" />
                Top Search Terms (Last 30 Days)
              </h3>
              <div className="space-y-2">
                {paidSearchIntel.topSearchTerms.slice(0, 5).map((term: any, idx: number) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{term.search_term}</p>
                      <p className="text-xs text-gray-500">
                        {term.impressions} impressions · {term.clicks} clicks
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-gray-900">{term.ctr?.toFixed(2)}%</p>
                      <p className="text-xs text-gray-500">CTR</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Top Performing Keywords</h3>
              <div className="space-y-2">
                {paidSearchIntel.topKeywords.slice(0, 5).map((kw: any, idx: number) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">{kw.keyword_text}</p>
                      <p className="text-xs text-gray-500">
                        {kw.conversions} conversions · ${((kw.cost_micros || 0) / 1000000).toFixed(2)} spend
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-green-600">{kw.conversions}</p>
                      <p className="text-xs text-gray-500">Conv.</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg shadow-sm p-6 text-white">
        <h2 className="text-xl font-semibold mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link
            to="/web-vitals"
            className="bg-white/10 hover:bg-white/20 rounded-lg p-4 transition-colors"
          >
            <Activity className="w-6 h-6 mb-2" />
            <h3 className="font-medium mb-1">Check Web Vitals</h3>
            <p className="text-sm text-white/80">Monitor site performance</p>
          </Link>
          <Link
            to="/competitor-heatmap"
            className="bg-white/10 hover:bg-white/20 rounded-lg p-4 transition-colors"
          >
            <MapPin className="w-6 h-6 mb-2" />
            <h3 className="font-medium mb-1">Scan Competitors</h3>
            <p className="text-sm text-white/80">Track HVAC businesses</p>
          </Link>
          <Link
            to="/keyword-demand"
            className="bg-white/10 hover:bg-white/20 rounded-lg p-4 transition-colors"
          >
            <TrendingUp className="w-6 h-6 mb-2" />
            <h3 className="font-medium mb-1">Keyword Demand</h3>
            <p className="text-sm text-white/80">Track HVAC keywords</p>
          </Link>
        </div>
      </div>
    </div>
  );
}
